import { Input } from '@core-ui/Components/ui/input.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import {
  IconArrowRight,
  IconEdit,
  IconPlus,
  IconTrash,
  IconReload,
  IconDeviceFloppy
} from '@tabler/icons-react';
import { getCurrentUser } from '@features/auth';
import type {
  ApiResponse,
  SelectOption,
  FormattedDisposition,
  IntegrationAccount,
  IntegrationDisposition,
  PatchMappingDto,
  DispositionAbbreviation,
  Mapping,
  CreateDispositionDto,
  UpdateDispositionDto,
  OutreachDisposition,
  DispositionRecord
} from '@types';
import { transformOutreachMappings } from '@utils/transformOutreachMappings.ts';
import { InputSelect, InputSelectTrigger } from '@ui/shared/customSelect.tsx';
import { logError } from '@lib/logger/logError.ts';
import { logSuccess } from '@lib/logger/logSuccess.tsx';
import { MappingDialog } from '@features/Settings/components/integrations/MappingDialog.tsx';
import { IntegrationMappingModalModes } from '@features/Settings/enums/IntegrationsEnums.ts';

interface OutreachMappingsComponentProps {
  fetchAccountsCallback: (userId: string, organizationId: string)=> Promise<ApiResponse<IntegrationAccount[]>>,
  fetchMappingsCallback: (organizationId: string)=> Promise<ApiResponse<Mapping>>,
  getDispositionsCallback: (organizationId: string, nonce: string)=> Promise<ApiResponse<IntegrationDisposition[]>>,
  createDispositionCallback?: (organizationId: string, nonce: string, createOutreachMappingDto: CreateDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>,
  updateDispositionCallback?: (organizationId: string, nonce: string, updateOutreachMappingDto: UpdateDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>,
  deleteDispositionCallback?: (organizationId: string, nonce: string, callDispositionId: number)=> Promise<ApiResponse<void>>,
  patchMappingCallback: (id: number, patchMappingDto: PatchMappingDto)=> Promise<ApiResponse<IntegrationDisposition>>,
  creationEnabled: boolean,
  editionEnabled: boolean,
  deletionEnabled: boolean,
}

export function OutreachMappingsComponent (
  {
    fetchAccountsCallback,
    fetchMappingsCallback,
    getDispositionsCallback,
    createDispositionCallback,
    updateDispositionCallback,
    deleteDispositionCallback,
    patchMappingCallback,
    creationEnabled,
    editionEnabled, 
    deletionEnabled
  }: OutreachMappingsComponentProps): React.JSX.Element {
  const { t } = useTranslation();
  const currentUser = getCurrentUser();

  const [isSearchingMappings, setIsSearchingMappings] = useState<boolean>(false);
  const [customDispositions, setCustomDispositions] = useState<IntegrationDisposition[]>([]);
  const [mappings, setMappings] = useState<FormattedDisposition[]>([]);
  const [selectedDisposition, setSelectedDisposition] = useState('');
  const [selectedDispositionId, setSelectedDispositionId] = useState(0);
  const [mappingsForm, setMappingsForm] = useState<Map<DispositionAbbreviation, string>>(new Map<DispositionAbbreviation, string>);
  const [dispositionWasChanged, setDispositionWasChanged] = useState('' as DispositionAbbreviation);
  const [mappingValue, setMappingValue] = useState('');
  const nonce = useRef('');
  const completeMappings = useRef<FormattedDisposition[]>();
  const [tableKey, setTableKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [componentVersion, setComponentVersion] = useState(0);

  function findDispositionId (name: string): void {
    const d = customDispositions.find((disp)=>{
      return disp.attributes.name === name;
    });
    setSelectedDispositionId(Number(d?.id));
  }

  function handleDispositionSearch (event: React.FormEvent<HTMLInputElement>): void {
    let search = '';
    if (event.target !== null) {
      search = (event.target as HTMLInputElement).value;
    }
    setIsSearchingMappings(search !== '');

    const filteredMappings = mappings
      .filter((mapping) =>
        mapping.defaultAction.toLowerCase().includes(search.toLowerCase())
      );

    setMappings(filteredMappings);
  }


  async function fetchDispositions (
    fetchAccountsCallback: (userId: string, organizationId: string)=> Promise<ApiResponse<IntegrationAccount[]>>,
    getDispositionsCallback: (organizationId: string, nonce: string)=> Promise<ApiResponse<IntegrationDisposition[]>>
  ): Promise<IntegrationDisposition[] | undefined> {
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }
    const { userId, organizationId } = currentUser;

    const { data: accounts }: ApiResponse<IntegrationAccount[]> = await fetchAccountsCallback(userId, organizationId);

    if (accounts !== undefined && accounts.length !== 0) {
      nonce.current = accounts[0].nonce;
      const { data } = await getDispositionsCallback(organizationId, nonce.current);

      setCustomDispositions(data ?? []);
      return data;
    }
  }

  async function fetchMappings (
    fetchMappingsCallback: (organizationId: string)=> Promise<ApiResponse<Mapping>>
  ): Promise<void> {
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }

    const { organizationId } = currentUser;
    const { data: fetchedMappings } = await fetchMappingsCallback(organizationId);


    let callDispositions = customDispositions;

    if (customDispositions.length <= 0) {
      const data = await fetchDispositions(fetchAccountsCallback, getDispositionsCallback);
      if (data !== undefined) {
        callDispositions = data;
      }


      if (fetchedMappings !== undefined && callDispositions.length !== 0) {
        const { id, mapping } = fetchedMappings;

        const transformedMapping = transformOutreachMappings(
          mapping as DispositionRecord,
          callDispositions,
          id
        );

        completeMappings.current = transformedMapping;

        for (const formattedDisposition of transformedMapping) {
          mappingsForm.set(formattedDisposition.abbreviation, formattedDisposition.defaultName);
        }
        setMappingsForm(mappingsForm);
        setMappings(transformedMapping);
      }
    }
  }
  
  async function patchMapping (
    patchMappingCallback: (id: number, patchMappingDto: PatchMappingDto)=> Promise<ApiResponse<IntegrationDisposition>>
  ): Promise<void> {
    if (mappings.length === 0 ) {
      throw new Error('No mappings found.');
    }

    const dispositionId = mappings[0].dispositionId;

    const patchMappingDto: PatchMappingDto = {
      mapping: JSON.stringify(Object.fromEntries(mappingsForm))
    };

    const response = await patchMappingCallback( 
      dispositionId, 
      patchMappingDto
    );

    if (response === undefined) {
      await logError(
        t('settings.integrationsPages.mappings.patchErrorToast'),
        patchMapping.name,
        { dispositionId, patchMappingDto },
        true);
      return;
    }
    await logSuccess(
      t('settings.integrationsPages.mappings.patchSuccessToast'),
      patchMapping.name,
      { dispositionId, patchMappingDto },
      true);
  }

  function setUserPermissions (): void {
    if (currentUser !== null) {
      creationEnabled = currentUser.isSuperAdmin || currentUser.isAdmin;
      editionEnabled = currentUser.isSuperAdmin || currentUser.isAdmin;
      deletionEnabled = currentUser.isSuperAdmin || currentUser.isAdmin;
    }
  }

  const reload = async (): Promise<void> => {
    setIsLoading(true);
    setTableKey(prev => prev + 1);

    await fetchMappings(fetchMappingsCallback);
    await fetchDispositions(fetchAccountsCallback, getDispositionsCallback);

    setIsLoading(false);
  };

  const remap = (): void => {
    setTableKey(prev => prev + 1);
    setComponentVersion((prev) => prev + 1);
  };

  useEffect(()=> {
    setUserPermissions();
  }, [currentUser]);

  useEffect(() => {
    if (!isSearchingMappings) {
      void reload();
    }
  }, [isSearchingMappings, completeMappings]);

  return (
    <div className="grid items-center gap-4">
      <section title="mapping-controls" className="flex justify-start items-center gap-4">
        <form title="search-mapping-form" className="flex justify-start items-center gap-4 w-full">
          <Input
            id="mapping-search"
            className="w-1/3"
            placeholder={t('settings.integrationsPages.mappings.searchDispositionPlaceholder')}
            onInput={handleDispositionSearch}
            data-qa-id="search-mapping-input"
          ></Input>
          {/* TODO: Select by category when categories are defined */}
          <Button
            type="reset"
            variant="outline"
            onClick={()=>{
              setIsSearchingMappings(false);
              setMappings(completeMappings.current ?? []);
            }}
            data-qa-id="reset-mapping-search">{t('settings.integrationsPages.mappings.reset')}
          </Button>
        </form>
        {creationEnabled && 
            <MappingDialog
              trigger={<Button>
                <IconPlus className="text-foreground"/>{t('settings.integrationsPages.mappings.createModalTitle')}
              </Button>}
              mode={IntegrationMappingModalModes.Create}
              nonce={nonce.current}
              refreshCallback={fetchMappingsCallback}
              createCallback={createDispositionCallback}
            />}
        <Button
          disabled={selectedDisposition === ''}
          onClick={()=> {
            void patchMapping(patchMappingCallback).then(()=> {
              setSelectedDisposition('');
            });
          }}
          data-qa-id="save-mapping-button"
        ><IconDeviceFloppy/>{t('settings.integrationsPages.mappings.saveDisposition')}</Button>
        <Button
          onClick={()=> {
            void reload();
            remap();
          }}
          disabled={isLoading}
          data-qa-id="sync-mapping-button"
        ><IconReload />{t('settings.integrationsPages.mappings.refreshMappings')}</Button>
        
      </section>
      <section key={componentVersion} title="mappings-listing">
        <div className="flex w-full overflow-y-auto h-[30vh]">
          <Table key={tableKey}>
            <TableHeader className="sticky top-0">
              <TableRow>
                <TableHead className="text-foreground hover:bg-background p-0" dir="ltr">
                  <div className="rounded-s-md bg-gray-200 dark:bg-gray-700 p-2">
                    {t('settings.integrationsPages.mappings.table.tendrilDisposition')}
                  </div>
                </TableHead>
                <TableHead className="w-1/5 text-foreground p-0">
                  <div className="bg-gray-200 dark:bg-gray-700 p-2">
                    {t('settings.integrationsPages.mappings.table.mappedTo')}
                  </div>
                </TableHead>
                <TableHead className="text-foreground p-0" dir="rtl">
                  <div className="rounded-s-md bg-gray-200 dark:bg-gray-700 p-2">
                    {t('settings.integrationsPages.mappings.table.customDisposition')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="border-0">
              {mappings.map((mapping, index) => (
                <TableRow key={`mapping-row-${index}`}
                  className="border-t-0 border-border/60 hover:bg-transparent p-0 mb-1">
                  <td className="px-3 w-1/2">{mapping.defaultAction}</td>
                  <td className="px-3 w-1/5"><IconArrowRight/></td>
                  <td className="px-3 flex items-center gap-6">
                    <InputSelect
                      placeholder={mapping.customName ?? mapping.defaultName}
                      clearable={true}
                      options={customDispositions.reduce<SelectOption[]>((options: SelectOption[], customDisposition) => {
                        const customName = mapping.id !== customDisposition.id
                          ? customDisposition.attributes.name
                          : mapping.customName;
                        const value = customDisposition.attributes.name;
                        options.push({ value, label: customName });
                        return options;
                      }, new Array<SelectOption>())
                      }
                      onValueChange={(val: string) => {
                        setSelectedDisposition(val);
                        const updatedMap = mappingsForm.set(mapping.abbreviation, val);
                        setMappingsForm(updatedMap);
                        setDispositionWasChanged(mapping.abbreviation);
                        setMappingValue(val);
                        findDispositionId(val);
                      }}
                      value={'' ?? mappingsForm?.get(mapping.abbreviation)}
                    >
                      {({
                        options,
                        placeholder,
                        clearable,
                        selectedValue,
                        isPopoverOpen,
                        setIsPopoverOpen,
                        onClearAllOptions,
                      }) => (
                        <InputSelectTrigger
                          options={options}
                          placeholder={placeholder}
                          clearable={clearable}
                          disabled={options === null}
                          selectedValue={selectedValue}
                          isPopoverOpen={isPopoverOpen}
                          setIsPopoverOpen={setIsPopoverOpen}
                          onClearAllOptions={onClearAllOptions}
                          className="w-3/4 rounded-md px-3 py-2 text-left bg-muted hover:bg-muted/50 border mt-2 mb-2"
                        />
                      )}
                    </InputSelect>
                    {selectedDisposition !== '' && dispositionWasChanged === mapping.abbreviation &&
                       <div className="flex gap-4">
                         {editionEnabled && 
                             <MappingDialog
                               trigger={
                                 <IconEdit className="text-foreground"/>}
                               mode={IntegrationMappingModalModes.Edit}
                               nonce={nonce.current}
                               currentDisposition={mapping}
                               dispositionId={selectedDispositionId}
                               refreshCallback={fetchMappingsCallback}
                               mappingValue = {mappingValue}
                               updateCallback={updateDispositionCallback}
                             />}
                         {deletionEnabled && 
                             <MappingDialog
                               trigger={<IconTrash className="text-destructive hover:text-foreground"/>}
                               nonce={nonce.current}
                               mode={IntegrationMappingModalModes.Delete}
                               dispositionId={selectedDispositionId}
                               refreshCallback={fetchMappingsCallback}
                               deleteCallback={deleteDispositionCallback}
                             />}
                       </div>
                    }
                  </td>
                </TableRow>
              ))}
             
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  )
  ;
}